import { memo, useState } from 'react';

import { isLoggedIn } from '@audacy-clients/core/atoms/auth';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { readOneTrustCookieAndUpdateAdPreferences } from '~/backend';
import AdPreferencesModal from '~/components/Account/AdPreferencesModal';
import BaseLink from '~/components/BaseLink/';
import IconButton from '~/components/Button/IconButton';
import FooterLink from '~/components/Link/FooterLink';
import { Mixins } from '~/styles';
import { LinkType } from '~/types/links';
import { getPlatformIcon, getSocialIcon } from '~/utils/icons';

import styles from './styles';
import type {
  IDownloadAppLinkProps,
  IFooterLink,
  ILegalLinkProps,
  IMainLinkGroup,
  IMainLinkProps,
  ISocialLinkProps,
} from './types';

interface IFooterProps {
  sections: {
    downloadApp: IFooterLink[];
    legal: IFooterLink[];
    main: IMainLinkGroup[];
    social: IFooterLink[];
  };
}

const DownloadAppLinks = ({ links }: IDownloadAppLinkProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <ul aria-label={t('footer.ariaLabels.downloadApp')}>
      {links?.map((link: IFooterLink) => {
        const icon = getPlatformIcon(link.id);
        return (
          !!icon && (
            <li key={link.id} css={styles.downloadAppLinkItem}>
              <BaseLink aria-label={link.label} href={link.href} linkCss={styles.downloadAppLink}>
                <span css={styles.downloadAppLink} aria-hidden={true}>
                  {icon}
                </span>
              </BaseLink>
            </li>
          )
        );
      })}
    </ul>
  );
};

const SocialLinks = ({ links }: ISocialLinkProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <ul aria-label={t('footer.ariaLabels.socialMedia')}>
      {links?.map((link: IFooterLink) => {
        const icon = getSocialIcon(link.id);
        return (
          <li key={link.id} css={styles.socialLink}>
            {!!icon && (
              <IconButton
                ariaLabel={link.label}
                as={LinkType.Anchor}
                href={link.href}
                icon={icon}
                isExternal={true}
                iconSizeInPx={30}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};

const MainLinks = ({ linkGroups }: IMainLinkProps): JSX.Element => {
  const [isAdPreferencesModalOpen, setIsAdPreferencesModalOpen] = useState(false);
  const openAdPreferencesModal = () => setIsAdPreferencesModalOpen(true);
  const loggedIn = useRecoilValue(isLoggedIn);
  let adPrefOnClick = openAdPreferencesModal;

  // if unauthenticated, Ad preferences link should open OneTrust modal
  if (!loggedIn) {
    adPrefOnClick = () => {
      // functionality below is available only after OT head scripts attach OneTrust SDK to window
      // https://developer.onetrust.com/onetrust/docs/javascript-api
      if (window.OneTrust) {
        // uses OneTrust SDK to open OneTrust modal
        window.OneTrust.ToggleInfoDisplay();
        // uses OneTrust SDK to run read/update function whenever user preferences change on cookie
        window.OneTrust.OnConsentChanged(readOneTrustCookieAndUpdateAdPreferences);
      }
    };
  }

  return (
    <>
      {linkGroups?.map((linkGroup: IMainLinkGroup, i) => {
        const { title, links } = linkGroup;
        return (
          <div css={styles.mainTitleAndLinksContainer} key={`${title.id}_${i}`}>
            <h2 css={styles.mainTitle}>{title.label}</h2>
            <AdPreferencesModal
              isOpen={isAdPreferencesModalOpen}
              onClose={() => setIsAdPreferencesModalOpen(false)}
            />
            <ul aria-label={title.label}>
              {links.map((link: IFooterLink) =>
                link.id === 'doNotSellMyPersonalInfo' ? (
                  <li key={link.id} css={styles.mainLink}>
                    <FooterLink
                      isExternal={link.openInNewTab}
                      href={link.href}
                      label={link.label}
                      useClickHandler={link.useClickHandler}
                      onClick={adPrefOnClick}
                    />
                  </li>
                ) : (
                  <li key={link.id} css={styles.mainLink}>
                    <FooterLink
                      isExternal={link.openInNewTab}
                      href={link.href}
                      label={link.label}
                      useClickHandler={link.useClickHandler}
                      onClick={link.onClick}
                    />
                  </li>
                ),
              )}
            </ul>
          </div>
        );
      })}
    </>
  );
};

const LegalLinks = ({ links }: ILegalLinkProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <ul aria-label={t('footer.ariaLabels.legal')}>
      {links?.map((link: IFooterLink) => (
        <li key={link.id} css={styles.legalLinkContainer}>
          <FooterLink
            isExternal={link.openInNewTab}
            href={link.href}
            isSecondary
            label={link.label}
            linkCss={styles.legalLink}
          />
        </li>
      ))}
    </ul>
  );
};

const Footer = memo(function Footer({ sections }: IFooterProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div css={[Mixins.horizontalModulePadding(), styles.footerContainer]}>
      <div css={styles.externalAndMainContainer}>
        <nav aria-label={t('footer.ariaLabels.external')} css={styles.externalContainer}>
          <div css={styles.downloadAppContainer}>
            <DownloadAppLinks links={sections.downloadApp} />
          </div>
          <SocialLinks links={sections.social} />
        </nav>
        <nav aria-label={t('footer.ariaLabels.footer')} css={styles.mainContainer}>
          <MainLinks linkGroups={sections.main} />
        </nav>
      </div>
      <div css={styles.legalAndCopyrightContainer}>
        <nav aria-label={t('footer.ariaLabels.legal')} css={styles.legalContainer}>
          <LegalLinks links={sections.legal} />
        </nav>
        <div css={styles.copyright}>
          <p>
            {t('footer.copyright', {
              year: new Date().getFullYear(),
            })}
          </p>
        </div>
      </div>
    </div>
  );
});

export default Footer;
